import { Add } from "@mui/icons-material";
import {
    Alert,
    Button,
    Card,
    CardContent,
    CardHeader,
    CircularProgress,
    Grid,
    TextField,
    useTheme,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { GET, PATCH } from "../../Utilities/BaseService";
import SearchableInput from "../Dashboard/Components/SearchableInput";

const UpdateAdvancePayment = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const id = new URLSearchParams(location.search).get("id");

    const [feedback, setFeedback] = useState("");

    const [advance, setAdvance] = useState({
        meterNo: "",
        advance: "",
        paymentMethod: "",
    });

    const [payment, setPayment] = useState<any>("");

    const { data, isLoading } = useQuery(
        ["advance_single", id],
        () => GET("/advance/get", { id }),
        { enabled: Boolean(id) }
    );

    useEffect(() => {
        if (data?.data?.advance) {
            const a = data.data.advance;
            setAdvance({
                meterNo: a.meter?.meterNo || "",
                advance: String(a.advanceBalance || ""),
                paymentMethod: a.paymentMethod?.id || "",
            });
            setPayment(
                a.paymentMethod ? { "payment method": a.paymentMethod.id } : ""
            );
        }
    }, [data]);

    useEffect(() => {
        if (payment) {
            setAdvance((prev) => ({
                ...prev,
                paymentMethod: payment["payment method"] || prev.paymentMethod,
            }));
        }
    }, [payment]);

    const updateMutation = useMutation(
        (payload: any) => PATCH("/advance", payload, { id }),
        {
            onSuccess(res: any) {
                setFeedback(res.data?.message || "Advance updated");
                setTimeout(() => navigate("/advance"), 1000);
            },
            onError(err: any) {
                setFeedback(err.response?.data?.message || "Error occurred");
            },
        }
    );

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!advance.advance || !advance.paymentMethod) {
            setFeedback("Please fill all fields");
            return;
        }

        updateMutation.mutate({
            advance: Number(advance.advance),
            paymentMethod: advance.paymentMethod,
        });
    };

    return (
        <>
            <Card sx={{ bgcolor: theme.palette.common.white }}>
                <CardHeader title="Update Advance Balance" />
                <CardContent>
                    <Grid
                        container
                        component="form"
                        spacing={2}
                        onSubmit={handleSubmit}
                    >
                        <Grid item xs={12} md={4}>
                            <TextField
                                size="small"
                                label="Meter No."
                                fullWidth
                                required
                                value={advance.meterNo}
                                disabled
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <TextField
                                size="small"
                                label="Advance"
                                fullWidth
                                required
                                type="number"
                                value={advance.advance}
                                onChange={(e) =>
                                    setAdvance({
                                        ...advance,
                                        advance: e.target.value,
                                    })
                                }
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <SearchableInput
                                api="/reading/payment-method"
                                label="Payment Method"
                                multiple={false}
                                filter={payment}
                                setFilter={setPayment}
                                defaultValue={
                                    data?.data?.advance?.paymentMethod
                                }
                            />
                        </Grid>

                        <Grid item xs={12} md={4} sx={{ pt: 2 }}>
                            <Button
                                type="submit"
                                variant="outlined"
                                color="secondary"
                                startIcon={<Add />}
                                endIcon={
                                    updateMutation.isLoading ? (
                                        <CircularProgress size={16} />
                                    ) : null
                                }
                                disabled={updateMutation.isLoading || isLoading}
                            >
                                Update Advance Payment
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {feedback && (
                <Alert
                    severity={updateMutation.isError ? "error" : "success"}
                    sx={{ mt: 2 }}
                >
                    {feedback}
                </Alert>
            )}
        </>
    );
};

export default UpdateAdvancePayment;
